import { useEffect, useState } from 'react';
import {
  Alert, Box, Link, Skeleton, Typography, useTheme,
} from '@mui/material';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { CommunityEvent, CommunityEventLocationType } from '@optimus/models';
import { TestIds } from '../../../assets/testIds';
import { getNextShopTalkEvent, getUpcomingCommunityEvents } from '../../../api/communityAPI';
import { useAppSelector } from '../../../store/hooks';
import {
  selectGetMemberInformationStatus,
  selectMemberInformation,
  selectMemberSession,
} from '../../../store/slices/onboardingSlice';
import { isPaidTier } from '../../../common/utils/MembershipTierAccess';
import { SubscriptionTier } from '../../../common/models/Subscription';
import { useCustomEventTracking } from '../../../common/useCustomEventTracking';
import { Logger } from '../../../config/logging/logger';
import { useServiceNationUrl } from '../../../common/utils/useServiceNationUrl';
import { useNavigationUtils } from '../../../common/utils/useNavigationUtils';

const logger = Logger.create('HomeUpcoming');

const COMMUNITY_URL = 'https://connect.servicenation.com';
const SHOP_TALK_URL = `${COMMUNITY_URL}/c/shop-talk`;
const GENERAL_FORUM_URL = `${COMMUNITY_URL}/c/service-nation-connect`;
const EVENT_COUNT = 3;

interface UpcomingItem {
  key: string;
  title: string;
  linkText: string;
  url: string;
  details: string[];
}

const LOCATION_LABEL_KEYS: Partial<Record<CommunityEventLocationType, string>> = {
  live_stream: 'homePage.upcoming.location.liveStream',
  live_room: 'homePage.upcoming.location.liveRoom',
  virtual: 'homePage.upcoming.location.virtual',
  in_person: 'homePage.upcoming.location.inPerson',
};

function UpcomingRow({ item, onClick, disabled }: {
  item: UpcomingItem; onClick: () => void; disabled: boolean;
}) {
  const theme = useTheme();
  const { t } = useTranslation();
  const detailSx = {
    fontSize: 12, lineHeight: 1.66, letterSpacing: '0.4px', whiteSpace: 'nowrap',
  };

  return (
    <Box
      data-testid={TestIds.homePage.upcomingItem}
      sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(0.5) }}
    >
      <Typography sx={{
        fontSize: 15, fontWeight: 500, lineHeight: 1.57, letterSpacing: '0.1px', color: theme.palette.text.primary,
      }}>
        {item.title}
      </Typography>
      <Box sx={{
        display: 'flex', alignItems: 'center', flexWrap: 'wrap', columnGap: theme.spacing(1),
      }}>
        <Link
          component="button"
          type="button"
          disabled={disabled}
          underline="always"
          onClick={onClick}
          data-testid={TestIds.homePage.upcomingLink}
          // Several rows read "RSVP", so name each action after its row for screen readers
          aria-label={`${t('homePage.upcoming.linkLabel', {
            link: item.linkText, title: item.title, interpolation: { escapeValue: false },
          })}`}
          sx={{
            ...detailSx, color: theme.palette.brand.primary, textDecorationColor: 'currentColor', textAlign: 'left',
          }}
        >
          {item.linkText}
        </Link>
        {item.details.map((detail) => (
          <Box key={detail} sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1) }}>
            <Box sx={{
              width: 4, height: 4, borderRadius: '50%', backgroundColor: theme.palette.grey[200], flexShrink: 0,
            }} />
            <Typography sx={{ ...detailSx, color: theme.palette.grey[600] }}>{detail}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

/**
 * Home page Upcoming panel (SN-1047). Paid tiers other than Association see their
 * next core business discussions. Free and Association members, and eligible
 * members with no events, see Shop Talk and the General Forum link instead.
 */
export function HomeUpcoming() {
  const theme = useTheme();
  const { t } = useTranslation();
  const { trackEngagement } = useCustomEventTracking();
  const { buildServiceNationUrl } = useServiceNationUrl();
  const { openExternalLinkAsync } = useNavigationUtils();
  const [isOpening, setIsOpening] = useState(false);
  const [hasOpenError, setHasOpenError] = useState(false);
  const { id: memberId, company } = useAppSelector(selectMemberInformation);
  const memberInformationStatus = useAppSelector(selectGetMemberInformationStatus);
  const { memberId: sessionMemberId } = useAppSelector(selectMemberSession);
  // The tier lives on the company, so wait for it rather than treat a paid member
  // as free while their information is still loading. The member information is
  // persisted, so after another member logs in on the same browser it briefly
  // belongs to the previous member: wait until it matches the session.
  const isMemberLoaded = !!memberId && memberId === sessionMemberId &&
    !!company?.id && memberInformationStatus !== 'pending';
  const tier = company?.subscription?.subscriptionTier;
  // Association pays for membership but has the same Upcoming content as Free.
  const canViewCoreDiscussions = isPaidTier(tier) && tier !== SubscriptionTier.AM;
  // null while loading, including until the member and their tier are known
  const [events, setEvents] = useState<CommunityEvent[] | null>(null);
  // undefined while loading, null when no Shop Talk event is scheduled
  const [shopTalkEvent, setShopTalkEvent] = useState<CommunityEvent | null | undefined>(undefined);

  useEffect(() => {
    if (!isMemberLoaded) return undefined;
    if (!canViewCoreDiscussions) {
      setEvents([]);
      return undefined;
    }

    let isCurrent = true;
    setEvents(null);
    getUpcomingCommunityEvents(memberId)
      .then((upcoming) => {
        if (isCurrent) setEvents(upcoming.slice(0, EVENT_COUNT));
      })
      .catch((error) => {
        logger.error(Logger.format('Failed to load upcoming community events', (error as Error)?.message));
        if (isCurrent) setEvents([]);
      });
    return () => {
      isCurrent = false;
    };
  }, [canViewCoreDiscussions, memberId, isMemberLoaded]);

  // The community links show when there are no events to list
  const showsCommunityLinks = events?.length === 0;

  useEffect(() => {
    if (!showsCommunityLinks) return undefined;

    let isCurrent = true;
    getNextShopTalkEvent()
      .then((event) => {
        if (isCurrent) setShopTalkEvent(event);
      })
      .catch((error) => {
        logger.error(Logger.format('Failed to load the next Shop Talk event', (error as Error)?.message));
        if (isCurrent) setShopTalkEvent(null);
      });
    return () => {
      isCurrent = false;
    };
  }, [showsCommunityLinks]);

  const eventDetails = (event: CommunityEvent) => {
    const locationKey = event.locationType ? LOCATION_LABEL_KEYS[event.locationType] : undefined;
    return [
      ...(locationKey ? [t(locationKey)] : []),
      dayjs(event.startsAt).format('MMM D'),
    ];
  };

  const communityLinks: UpcomingItem[] = [
    {
      key: 'shop-talk',
      title: t('homePage.upcoming.shopTalk.title'),
      linkText: t('homePage.upcoming.shopTalk.link'),
      url: SHOP_TALK_URL,
      details: shopTalkEvent ? eventDetails(shopTalkEvent) : [],
    },
    {
      key: 'general-forum',
      title: t('homePage.upcoming.generalForum.title'),
      linkText: t('homePage.upcoming.generalForum.link'),
      url: GENERAL_FORUM_URL,
      details: [],
    },
  ];

  const eventItems: UpcomingItem[] = (events ?? []).map((event) => ({
    key: `${event.id}`,
    title: event.name,
    linkText: event.hostName ?
      // React escapes the text already; i18next escaping would show O'Connell as O&#39;Connell
      t('homePage.upcoming.hostInvite', {
        hostName: event.hostName, interpolation: { escapeValue: false },
      }) :
      t('homePage.upcoming.rsvp'),
    url: event.url,
    details: eventDetails(event),
  }));

  const items = eventItems.length ? eventItems : communityLinks;
  const isLoading = events === null || (showsCommunityLinks && shopTalkEvent === undefined);
  const skeletonCount = events === null ? EVENT_COUNT : communityLinks.length;

  const openCommunityItem = async (item: UpcomingItem) => {
    if (isOpening) return;
    trackEngagement('Home', 'Click', 'UpcomingItem', {
      item: eventItems.length ? 'communityEvent' : item.key,
    });
    setIsOpening(true);
    setHasOpenError(false);
    try {
      // Mint the five-minute JWT on click; never put it in the rendered page.
      await openExternalLinkAsync(() => buildServiceNationUrl('community', false, item.url));
    } catch (error) {
      // Log the message rather than the whole HTTP error, which may contain auth headers.
      logger.error(Logger.format('Failed to open Community page', (error as Error)?.message));
      setHasOpenError(true);
    } finally {
      setIsOpening(false);
    }
  };

  return (
    <Box
      data-testid={TestIds.homePage.upcoming}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(3),
        padding: theme.spacing(3),
        border: `1px solid ${theme.palette.grey[200]}`,
        borderRadius: theme.spacing(2),
        backgroundColor: theme.palette.common.white,
      }}
    >
      <Typography sx={{
        fontSize: 20, fontWeight: 500, lineHeight: 1.6, letterSpacing: '0.15px', color: theme.palette.text.primary,
      }}>
        {t('homePage.upcoming.heading')}
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(2.5) }}>
        {isLoading ?
          Array.from({ length: skeletonCount }, (_, index) => (
            <Skeleton key={index} variant="rounded" height={48} />
          )) :
          items.map((item) => (
            <UpcomingRow
              key={item.key}
              item={item}
              disabled={isOpening}
              onClick={() => openCommunityItem(item)}
            />
          ))}
      </Box>
      {hasOpenError && <Alert severity="error">{t('error.generalMessage')}</Alert>}
    </Box>
  );
}
